import { useAtomValue } from 'jotai';
import { useCallback, useRef } from 'react';
import { useParams as useParamsBase } from 'react-router-dom';

import { useTrackEvent as useTrackEventBase } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { OutstandingMeasures, visitorMeasuresAtom } from '../state';

export type Dependencies = {
  useParams: typeof useParamsBase;
  useTrackEvent: typeof useTrackEventBase;
  useVisitorMeasures: () => OutstandingMeasures;
};

export const DEFAULT_DEPENDENCIES: Dependencies = {
  useParams: useParamsBase,
  useTrackEvent: useTrackEventBase,
  useVisitorMeasures: () => useAtomValue(visitorMeasuresAtom),
};

/**
 * Returns a callback suitable for `MeasureConnected`'s `onValuesChangeCallback`
 * prop. Fires a `CLIENT_MEASURES_STARTED` Segment event exactly once -- the
 * first time the client interacts with any clinical measure radio button.
 *
 * The ref persists across paginated measure pages (since `PaginatedDrawer` does
 * not remount the content component on page changes), giving us fire-once-per-
 * session behavior.
 *
 * @param dependencies - The dependencies to use for the hook.
 */
export function useTrackMeasureStarted({
  useParams,
  useTrackEvent,
  useVisitorMeasures,
}: Dependencies = DEFAULT_DEPENDENCIES) {
  const hasTrackedRef = useRef(false);
  const { userInputted } = useTrackEvent();
  const { patientShortId = '' } = useParams();
  const visitorMeasures = useVisitorMeasures();

  return useCallback(() => {
    if (hasTrackedRef.current) return;
    hasTrackedRef.current = true;
    userInputted(EventTypeEnum.CLIENT_MEASURES_STARTED, {
      patientShortId,
      outstandingMeasureTypes: visitorMeasures?.map((m) => m.schema.type) ?? [],
    });
  }, [userInputted, patientShortId, visitorMeasures]);
}
